// Versioned manifest for the Vosk backend runtime assets, mirroring
// asrAssetManifest.js. Assets are hosted immutably under a same-origin CDN path
// (published from lexa-asr-assets) and cached by asrAssetCache.
//
// One runtime bundle: runtime 3.0.1 (WASM-SIMD, split bundle) — vosk.js UMD shell
// + sibling vosk.wasm (streaming-compiled), over a LAYERED model: a stable
// acoustic archive and a small decoding-graph archive, each independently
// versioned at its own component path (see the layer notes below).
//
// SIMD is REQUIRED, and there is deliberately no scalar fallback runtime: local
// ASR requires WASM SIMD at the enablement gate (localAsrEnablement condition 4,
// `blockedBy: 'noWasmSimd'`), so a device without it never preloads or
// initializes local ASR — it stays on server Kaldi, and that decision is logged
// once per session in `enablement.resolved`. The superseded scalar vosk-v2
// bundle (monolithic model, embedded wasm) remains published-and-frozen in
// lexa-asr-assets for the sessions still pinned to old builds, but this client
// no longer resolves it: it cannot consume the layered bundle, and its only
// biased-decode mechanism (Model.loadGraph) strands ~33 MB of WASM heap per
// graph swap (lexa-asr-assets#33).
//
// Precompressed (.br) URL adoption: the deploy pipeline uploads .br/.gz siblings
// with Content-Encoding headers; plain URLs are unchanged, so the client opts in
// per-URL. Runtime JS and wasm point at the .br siblings (served with
// text/javascript resp. application/wasm + Content-Encoding: br — script tags,
// instantiateStreaming, and the browser's transparent decode all work). The model
// archives stay plain: they must remain opaque gzip bytes for the runtime
// extractor. Cache Storage keys by URL, so every expectedFiles entry is the exact
// URL the runtime fetches (.br where adopted).
//
// The explicit `wasmUrl` is load-bearing, not just defense-in-depth: the worker's
// built-in fallback looks for a sibling literally named `vosk.wasm` next to the
// model archive and can never find `vosk.wasm.br`.

import { wasmSimdSupported } from './engine/runtime_checks';
import { recordLocalAsrFailure } from './localAsrLifecycle';

// ---------------------------------------------------------------------------
// Split-bundle layers (lexa-asr-assets#28), on the semver naming epoch (#36).
//
// THREE INDEPENDENTLY VERSIONED COMPONENTS, each at its own base:
// `asr-assets/<component>/<semver>/<component>-<semver>.*`.
//
//   runtime/3.0.1/  vosk.js + vosk.wasm
//   model/0.15.0/   model-0.15.0.tar.gz    (acoustic layer)
//   graph/1.0.0/    graph-1.0.0.tar.gz     (decoding-graph layer)
//
// Names carry COORDINATES, NEVER FEATURES: `graph-1.0.0.tar.gz` tells you where
// and what version, nothing about lineage — the `zoov`/`lookahead` lineage now
// lives only inside the companion manifest's `hclg_corpus`/`_note` fields. That
// is a real loss of local signal here: the old `-graph-zoov-r3` stem let the
// lineage-pairing pin in voskBiasedGraph.test.js compare two names that both
// carried the lineage, and it cannot do that any more (see the note there).
// The version stays in the FILENAME, not just the path, because the runtime
// derives its IDBFS extraction marker from the archive filename — a generic
// `graph.tar.gz` would silently reuse the previous version's marker.
//
// Every path below is now version-scoped, so the transitional flat `model/` and
// `vosk-v3/` dirs this client used before #36 are GONE from the bucket (deploy is
// `s5cmd sync --delete`; removing a dir from the asset repo removes it from the
// CDN). Those URLs serve the SPA index.html fallback, which asrAssetCache's
// HTML-poison guard refuses rather than caching. There is no partial migration:
// all three bases move together or local ASR is down.
//
// DO NOT ADOPT THE `-zoov-r1`, `-r2` or `-r3` MONOLITH MODEL ARCHIVES: all three
// are published but unloadable. (The `-r2`/`-r3` per-phrase CORPORA are separately
// defective — see VOSK_HCLG_ASSET_PATH in voskBiasedGraph.js — two different
// defects that happen to share revision numbers.) They were cut WITHOUT the
// `vosk-model-small-en-us-0.15/` wrapper directory, and the runtime's extractor
// hardcodes strip-first-component (vosk.post.js `extract(archive, path, true)`) —
// so `am/`/`graph/`/`ivector/` were themselves stripped, everything landed flat,
// Vosk fell back to its legacy flat layout and loaded no ivector extractor. Model
// construction survives; every recognizer create then fails with `Ivector feature
// dimension mismatch: got -1 but network expects 30`, which kills local ASR for the
// session. Invisible to the asset integrity gate because size/sha256 are correct —
// the bytes are fine, the internal layout is not. The split-bundle layers below are
// cut wrapped and are what this client loads; the monoliths are named here only so
// nobody reaches back for one.
//
// The model is two archives extracted by the worker into ONE IDBFS tree keyed on
// the ACOUSTIC archive's URL, each layer gated by its own extraction marker:
//
// - Acoustic layer (am/, ivector/, conf/): byte-identical across every lineage
//   shipped so far, downloaded once per device, EVER — by the WORKER, not the
//   precache: it is not in `expectedFiles` (TT-1313 A2), so IDBFS holds the only
//   persistent copy and Cache Storage never sees the 22.8 MB body. It lives at its own
//   `asr-assets/model/<semver>/` base — the documented lexa-asr-assets#31
//   exception to "every URL derives from assetBase" — precisely so a runtime
//   migration never changes its URL: the same-oid-in-many-dirs trick is free in
//   the repo but a fresh 22.8 MB per student on the device. Under #36 the model
//   semver mirrors upstream vosk (`small-en-us-0.15` -> `0.15.0`), so it moves
//   only if the acoustic model itself does.
// - Graph layer (graph/: HCLr, Gr, words.txt): the per-revision decoding graph,
//   now at its own `asr-assets/graph/<semver>/` base rather than inside the
//   runtime dir. That is the #31 exception generalized: a runtime migration no
//   longer re-downloads the graph layer either. A story-content push ships a new
//   graph semver (~1.5 MB) and the acoustic layer never re-ships.
//
// Lineage pairing is load-bearing — the per-phrase biased HCLGs are numbered against
// an archive's own graph/words.txt, and an FST from another lineage loads without
// error and decodes wrong words. It pairs on the SYMBOL TABLE, not the revision
// number, which is why the two revision numbers DISAGREE and that is correct:
//
// - The graph layer is `graph/1.0.0` — the SAME BYTES as the pre-#36
//   `-graph-zoov-r3` archive (sha256 e25f68b4…), re-cut under the semver scheme
//   before it was ever adopted. Its words.txt is unchanged (sha256 e3472855…,
//   36,038 symbols), which is why the graph did not need a new version.
// - `VOSK_HCLG_ASSET_PATH` (voskBiasedGraph.js) points at the date-stamped CORPUS
//   `vosk-browser-hclg/20260901-e3472855` — a REBUILD against that same words.txt,
//   whose sha256 the path restates in its `-e3472855` suffix. The older `-zoov-r2`/`-r3`
//   corpora were defective (relabel permutation dropped at build; r3 also carried
//   undeclared alternatives) and have been deleted from the CDN; never serve them.
//
// The pairing is declared in two places that agree on the symbol table: the corpus
// path's own words.txt-sha suffix (asserted in voskBiasedGraph.test.js) and the graph
// companion's `hclg_corpus.cdn_path` (`graph/1.0.0/graph-1.0.0.manifest.json`, which
// still names the older `vosk-browser-hclg-zoov-r4` — the frozen manifest cannot be
// re-cut without a graph bump, and words.txt did not change, so the shared
// words_txt_sha256 is the pairing that holds, not that stale cdn_path string).
// A future revision that DOES change words.txt moves the graph semver AND the
// corpus path in one change.
//
// Nothing here can verify an archive/companion pairing — that the companion
// actually DESCRIBES its archive is a property of the published bytes. For the
// GRAPH layer it is enforced at runtime by asrAssetCache.validateModelIntegrity
// (size_bytes/sha256 against the cached archive); a wrong pairing shows up as
// validationSizeMismatch and then purge -> re-download on every device, so check
// that log line when bumping the graph stem. The ACOUSTIC archive is never cached
// (see expectedFiles), so its integrity rests on the worker's extraction failing
// loudly (createModel rejects -> the session falls back to server ASR); its
// companion manifest is presence/parse-checked only, as the deploy pre-flight.
//
// Byte weights below are the DECODED sizes wire-verified on the CDN after the #36
// deploy (`content-length` on each plain URL). The archive sizes are unchanged
// from the pre-#36 artifacts — same bytes, new paths — while the companion
// manifests grew because #36 rewrote their provenance notes.
const AM_ASSET_BASE = '/asr-assets/model/0.15.0';
const AM_STEM = 'model-0.15.0';
const AM_BYTES = 22784225;
const AM_MANIFEST_BYTES = 3240;
const GRAPH_ASSET_BASE = '/asr-assets/graph/1.0.0';
const GRAPH_STEM = 'graph-1.0.0';
const GRAPH_BYTES = 1521237;
const GRAPH_MANIFEST_BYTES = 6341;

/**
 * Cache Storage bucket identity for the whole Vosk split bundle, DELIBERATELY
 * DECOUPLED from `runtimeVersion`.
 *
 * The bucket used to be `asr-model-runtime-<semver>`, which meant every runtime
 * bump renamed it and `evictStaleCaches` dropped the previous bucket wholesale —
 * re-downloading the 22.8 MB acoustic and 1.5 MB graph archives even though
 * `modelUrl` and `graphUrl` had not moved. That is pure waste: since the #36
 * semver epoch each component is versioned at its OWN path, so a runtime bump
 * genuinely changes only the two runtime URLs.
 *
 * Keying the bucket here instead lets `pruneStaleCacheEntries` do the work it
 * already does — it deletes any cached entry absent from `expectedFiles`, so the
 * superseded `vosk.js.br`/`vosk.wasm.br` go and the graph archive stays. A runtime
 * bump costs ~0.9 MB (.br) instead of ~24 MB, with no change to
 * `evictStaleCaches`. (Since TT-1313 A2 the acoustic archive is not cached at all,
 * so the same prune pass reclaims the pre-A2 22.8 MB copy on first load.)
 *
 * KEEP THE `asr-model-` PREFIX: `evictStaleCaches` and `clearAsrAssetCaches` scan
 * on it, and it is what keeps the NeMo backend's `asr-model-v1` bucket from
 * sitting beside this one (only one backend's assets are ever resident).
 *
 * WHAT THIS GIVES UP: an atomic swap on runtime bumps. `evictStaleCaches` runs
 * only after the new version validates, so under the old scheme reverting the
 * client left the previous bucket intact and instantly usable; now the superseded
 * runtime entries have been pruned and a revert re-fetches ~0.9 MB. That trade is
 * ~24 MB on every device on every successful bump (certain) against ~0.9 MB on a
 * revert (rare). Note the guarantee was never as broad as it looked: `cacheName`
 * was scoped to the RUNTIME version only, so model and graph bumps already
 * mutated the live bucket and already risked the wholesale `PURGE` below. This
 * extends an accepted risk to the last case that was exempt; it does not add a
 * new one.
 *
 * WHEN TO BUMP THE EPOCH. Bumping costs every device the full ~24 MB, so it is
 * deliberate, not hygiene. The rule follows from what prune can and cannot do:
 * it removes entries that are SUPERSEDED (the URL left `expectedFiles`), and it
 * is blind to entries that are WRONG (bad bytes at a URL still expected). Only
 * the second case needs the epoch.
 *
 *   NOT for component bumps — a model or graph version bump changes that layer's
 *   URL, so prune + refetch already handles it inside this bucket. Not for
 *   runtime bumps either; that is the whole point of this constant.
 *
 *   YES if a URL's bytes ever change underneath us. The scheme rests on assets
 *   being immutable per URL (deployed `Cache-Control: max-age=31536000,
 *   immutable`, and every rebuild lands in a new version dir). lexa-asr-assets'
 *   build-vosk-runtime has an `allow_overwrite` input that explicitly permits
 *   replacing an existing dir's `vosk.js`/`vosk.wasm` with DIFFERENT bytes — safe
 *   only on a dir that was never deployed. If that ever lands on a deployed dir,
 *   every device holds stale bytes at a URL still in `expectedFiles`: prune will
 *   not touch it, and validateCache will not catch it either (it integrity-checks
 *   the GRAPH archive; the runtime files are presence-checked only). The epoch is
 *   the only lever that fixes those devices.
 *
 *   YES for a change in cache semantics that makes existing entries unsafe rather
 *   than merely stale — e.g. reworking the CACHE_METADATA_SUFFIX companions that
 *   readCachedBodySize parses.
 *
 * The value is an opaque monotonic token. Do not encode meaning in it: anything
 * meaningful here would be a second source of truth for a version that already
 * lives in the URLs.
 *
 * ONE MANIFEST ONLY. `buildVoskManifest` is still shaped to produce N manifests
 * (it did, before the scalar fallback was retired), but this bucket key no longer
 * varies with its input. If a second manifest is ever built from it, whatever
 * distinguishes the two MUST be folded into the bucket key — otherwise they share
 * one bucket and pruneStaleCacheEntries ping-pong-deletes each other's runtime
 * entries on alternating loads, silently re-fetching ~0.9 MB every time.
 */
const BUNDLE_CACHE_EPOCH = 'vosk-bundle-1';

/**
 * Builds one immutable per-version split-bundle manifest. `approxBytes` is the
 * DECODED size (not the `.br` wire size): the progress meter counts decoded body
 * bytes — asrAssetCache's drainForProgress reads the transparently-decompressed
 * stream, and its already-cached shortcut credits approxBytes against the same
 * total — so the weights must be in the same units. Used only to weight the
 * download-progress bar; validation is separate (per-layer companion manifests).
 *
 * `assetBase` is the RUNTIME component only. Under the #36 semver epoch NEITHER
 * model layer derives from it: the acoustic archive sits at `AM_ASSET_BASE` and
 * the graph archive at `GRAPH_ASSET_BASE`, each independently versioned, so a
 * runtime migration changes no model URL at all. `modelUrl` must stay the
 * acoustic archive: the worker keys its persisted IDBFS model path on `modelUrl`
 * alone, which is what lets the extracted acoustic tree survive graph bumps.
 *
 * `runtimeVersion` is the runtime semver; `version` is the derived identity label
 * used for TELEMETRY ONLY, so it still moves with every runtime bump and log
 * continuity is preserved. "Telemetry only" is the invariant, and its reach is
 * wider than one call site: asrAssetCache logs it on ready/validationFailed, and
 * it also leaves this module via getVoskCreateOptions().modelVersion ->
 * VoskAsr.modelVersion -> the handler's runtime.initSucceeded event. Nothing
 * branches on it anywhere. It is
 * deliberately slash-free (`runtime-3.0.1`, not `runtime/3.0.1`). The cache bucket
 * is NOT derived from it — see `BUNDLE_CACHE_EPOCH`.
 * @param {{runtimeVersion: string, runtimeBytes: number, wasmBytes: number}} spec
 * @returns {object}
 */
function buildVoskManifest({ runtimeVersion, runtimeBytes, wasmBytes }) {
  const version = `runtime-${runtimeVersion}`;
  const assetBase = `/asr-assets/runtime/${runtimeVersion}`;
  const runtimeUrl = `${assetBase}/vosk.js.br`;
  const wasmUrl = `${assetBase}/vosk.wasm.br`;
  const modelUrl = `${AM_ASSET_BASE}/${AM_STEM}.tar.gz`;
  const modelManifestUrl = `${AM_ASSET_BASE}/${AM_STEM}.manifest.json`;
  const graphUrl = `${GRAPH_ASSET_BASE}/${GRAPH_STEM}.tar.gz`;
  const graphManifestUrl = `${GRAPH_ASSET_BASE}/${GRAPH_STEM}.manifest.json`;
  // graphUrl and graphManifestUrl must stay PAIRED: the invariant is load-bearing
  // across two modules that read it differently — asrAssetCache validates the graph
  // layer only when BOTH URLs exist, while VoskAsr enables biased decode on graphUrl
  // alone, so a lone graphUrl would run biased decode against an archive whose
  // integrity was never checked, with no signal. Enforced in voskAssetManifest.test.js
  // (shared stem, both under GRAPH_ASSET_BASE, both in expectedFiles) rather than
  // here: this function runs at module evaluation on a static import chain from App.js, so
  // a throw would take down the bundle instead of degrading to server Kaldi — the
  // failure mode the rest of this module is careful to preserve.
  return Object.freeze({
    version,
    /**
     * Epoch-scoped, NOT version-scoped — the whole point of `BUNDLE_CACHE_EPOCH`
     * above, which carries the rationale and the trade-off. The `asr-model-`
     * prefix is what makes asrAssetCache treat this as an ASR bucket and evict
     * the other backend's.
     *
     * The property to preserve: a RUNTIME bump must not move this string. If it
     * ever does again, `evictStaleCaches` drops the bucket and both model
     * archives (~24 MB) come down for a wasm rebuild that changed neither of
     * their URLs. Pinned in voskAssetManifest.test.js.
     *
     * This bump (3.0.0 -> 3.0.1) still pays the full eviction once, because
     * devices are on the old `asr-model-runtime-3.0.0` name and that bucket is
     * what gets dropped. It is the last time: from here the bucket stops moving.
     */
    cacheName: `asr-model-${BUNDLE_CACHE_EPOCH}`,
    assetBase,
    runtimeUrl,
    wasmUrl,
    modelUrl,
    modelManifestUrl,
    // The acoustic archive is not in expectedFiles (below), so its size is carried here
    // for App's gated-boot planner: a cold device's bar estimate is this over the link
    // speed measured on the small precache (TT-1313).
    modelArchiveBytes: AM_BYTES,
    graphUrl,
    graphManifestUrl,
    expectedFiles: Object.freeze([
      Object.freeze({ url: modelManifestUrl, approxBytes: AM_MANIFEST_BYTES }),
      Object.freeze({ url: graphManifestUrl, approxBytes: GRAPH_MANIFEST_BYTES }),
      Object.freeze({ url: runtimeUrl, approxBytes: runtimeBytes }),
      Object.freeze({ url: wasmUrl, approxBytes: wasmBytes }),
      // The acoustic archive (modelUrl, 22.8 MB) is DELIBERATELY absent (TT-1313
      // A2): the worker downloads it through the cache-first service worker and
      // extracts it straight into IDBFS, which is its only persistent copy. Caching
      // it here too doubled the footprint (~52 MB) and the Cache.put of that one
      // body was the write that failed on full-disk Chromebooks. Its companion
      // manifest above stays as the deploy pre-flight for the layer.
      Object.freeze({ url: graphUrl, approxBytes: GRAPH_BYTES }),
    ]),
  });
}

/** WASM-SIMD split runtime over the layered (acoustic + graph) model bundle.
 * runtime 3.x = vosk-v2.2 plus the split-bundle worker (`graphUrl` load) and the
 * per-recognizer graph binding (one-step biased create, no Model.loadGraph).
 *
 * 3.0.1 is a PATCH rebuild of 3.0.0 — same client contract, nothing to adapt to.
 * `vosk.js` is byte-identical (same sha256); only `vosk.wasm` changed, rebuilt
 * under builder image `simd-2` with the hand-vectorized wasm-SIMD f32x4 BLAS
 * kernels from lexa-asr-assets#38. The prior "SIMD" runtime compiled with
 * `-msimd128` but left the nnet3 AM GEMM scalar (LLVM will not vectorize an FP
 * reduction without `-ffast-math`), which is why 3.0.0 decoded no faster than the
 * scalar build; #38 vectorizes sgemm/sgemv/sdot/saxpy by hand instead.
 *
 * Decode is NOT bit-identical to 3.0.0: the multi-accumulator reduction reorders
 * float summation. It is measurably CLOSER to a double-precision oracle than the
 * scalar path it replaces (~2-3x lower relative error on the model's TDNN-F
 * shapes), so the adoption gate is no-WER-regression, not transcript identity.
 *
 * `runtimeBytes`/`wasmBytes` are the published DECODED sizes (lexa-asr-assets#43:
 * `vosk.js` 348,008 B, sha256 `3ee15f22…`; `vosk.wasm` 3,224,149 B, LFS oid
 * `8c75d3de…`). They weight the download-progress bar ONLY — the runtime is not
 * integrity-validated client-side (after A2 getArchiveLayers covers the graph layer
 * alone — the acoustic archive left expectedFiles and is validated via its companion
 * manifest, not size/sha — and no runtime companion manifest is ever fetched) — so a
 * stale value skews the bar, not correctness. */
export const VOSK_ASSET_MANIFEST_SIMD = buildVoskManifest({
  runtimeVersion: '3.0.1',
  runtimeBytes: 348008,
  wasmBytes: 3224149,
});

/** Latched on first resolution so the no-SIMD failure below logs once, and so
 * the pre-cache gate and the later createModel call always see the same object. */
let resolvedManifest = null;

/**
 * Resolves the active Vosk manifest: always the SIMD runtime-3.0.1 manifest — there is
 * no scalar fallback. A device without WASM SIMD must never get this far: the
 * enablement gate (localAsrEnablement, `blockedBy: 'noWasmSimd'`) keeps local
 * ASR off entirely and the session falls back to server Kaldi, logged in
 * `enablement.resolved`. If this is reached anyway (a gate regression), record a
 * loud failure once — the v3 runtime cannot instantiate without SIMD, so the
 * session will land on server Kaldi via the init-failure path regardless, and
 * this line is what ties that failure to its real cause.
 * @returns {typeof VOSK_ASSET_MANIFEST_SIMD}
 */
export function resolveVoskAssetManifest() {
  if (!resolvedManifest) {
    resolvedManifest = VOSK_ASSET_MANIFEST_SIMD;
    if (!wasmSimdSupported()) {
      recordLocalAsrFailure('assets.simdRequiredButUnavailable', {
        version: resolvedManifest.version,
        fallbackTo: 'serverKaldi',
        expectedGate: 'enablement.noWasmSimd',
      });
    }
  }
  return resolvedManifest;
}

/**
 * Options for createVoskAsr(): the hosted runtime + model URLs for the resolved
 * manifest. `graphUrl` is the split bundle's decoding-graph layer — its presence
 * is also what tells VoskAsr the runtime can bind per-recognizer biased graphs.
 * `modelUrl` must be the acoustic archive (see buildVoskManifest).
 * @returns {{runtimeUrl: string, modelUrl: string, wasmUrl: string,
 *   graphUrl: string, modelVersion: string}}
 */
export function getVoskCreateOptions() {
  const manifest = resolveVoskAssetManifest();
  return {
    runtimeUrl: manifest.runtimeUrl,
    modelUrl: manifest.modelUrl,
    wasmUrl: manifest.wasmUrl,
    graphUrl: manifest.graphUrl,
    modelVersion: manifest.version,
  };
}
